
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { getSubscriptionLimits, getUpgradeMessage, type SubscriptionTier } from "@/utils/subscriptionUtils";

export const useSubscriptionStatus = () => {
  const { user } = useAuth();
  const [showLimitModal, setShowLimitModal] = useState(false);

  const { data: userProfile, refetch: refetchProfile } = useQuery({
    queryKey: ['user-profile', user?.id],
    queryFn: async () => {
      if (!user) return null;
      
      const { data, error } = await supabase
        .from('user_profiles')
        .select('subscription_tier')
        .eq('user_id', user.id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!user
  });

  // Get current tier directly from database (no conversion needed)
  const currentTier = (userProfile?.subscription_tier as SubscriptionTier) || 'alpha200';
  const subscriptionLimits = getSubscriptionLimits(currentTier);
  const profileLimit = subscriptionLimits.dailyProfiles;

  const checkSubscriptionLimit = (profileCount: number) => {
    const hasHitLimit = profileCount >= profileLimit && profileLimit < 999;
    
    if (hasHitLimit && currentTier === 'alpha200') {
      setShowLimitModal(true);
    }
    
    return hasHitLimit;
  };

  const getUpgradeMessageText = () => {
    return getUpgradeMessage(currentTier);
  };

  // Refetch profile data when subscription changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refetchProfile();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refetchProfile]);

  return {
    userProfile,
    profileLimit,
    showLimitModal,
    setShowLimitModal,
    checkSubscriptionLimit,
    getUpgradeMessageText,
    refetchProfile
  };
};
